import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import PhonelinkSetupIcon from "@mui/icons-material/PhonelinkSetup";
import NoEncryptionIcon from "@mui/icons-material/NoEncryption";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isSubAdminEmail } from "../../../functions/helperFunctions";
import { updateCustomersRow } from "../../../store/actions/dataActions";
import RenderCustomerContractors from "./RenderCustomerContractors";
import RenderDiscounts from "./RenderDiscounts";
import RenderUsageChart from "./RenderUsageChart";
import RenderCustomerPermits from "./RenderCustomerPermits";
import RemoteConnectionModal from "./managementModals/RemoteConnectionModal";

const CustomerInfoCard = ({
  customerSelectedRow,
  setCustomerSelectedRow,
  packagePage,
  setPackagePage,
  packagePageSize,
  setpackagePageSize,
  packageLoading,
  setPackageLoading,
  packageRowCount,
  setPackageRowCount,
  selectedPackage,
  setSelectedPackage,
  customerInfoCardRef,
}) => {
  const dispatch = useDispatch();
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [tables, setTables] = useState([]);
  const [cardPage, setCardPage] = useState(1);
  const [discountPage, setDiscountPage] = useState(1);
  const [remoteModalOpen, setRemoteModalOpen] = useState(false);
  const [remoteCredentials, setRemoteCredentials] = useState({
    username: "",
    usernameError: false,
    password: "",
    passwordError: false,
  });
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [connectionMsg, setConnectionMsg] = useState("");
  const [remove2faDialogOpen, setRemove2faDialogOpen] = useState(false);
  const [remove2faLoading, setRemove2faLoading] = useState(false);
  const [remove2faMsg, setRemove2faMsg] = useState("");
  const [convertEnterpriseDialogOpen, setConvertEnterpriseDialogOpen] =
    useState(false);
  const [convertEnterpriseLoading, setConvertEnterpriseLoading] =
    useState(false);
  const [convertEnterpriseMsg, setConvertEnterpriseMsg] = useState("");

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const { subAdmins } = useSelector((state) => state.nocData || {});
  const customersPagination = useSelector(
    (state) => state.nocData?.customers?.pagination,
  );
  const { subAdminDomains = [] } = subAdmins || {};

  const closeRemoteModal = () => {
    setRemoteModalOpen(false);
    setRemoteCredentials({
      username: "",
      usernameError: false,
      password: "",
      passwordError: false,
    });
    setConnectionMsg("");
  };

  const handleRemoteConnect = async () => {
    try {
      setConnectionLoading(true);
      setConnectionMsg("");

      // Clear previous errors
      setRemoteCredentials((prev) => ({
        ...prev,
        usernameError: false,
        passwordError: false,
      }));

      // Validate inputs
      if (!remoteCredentials.username.trim()) {
        setRemoteCredentials((prev) => ({ ...prev, usernameError: true }));
        return;
      }

      if (!remoteCredentials.password.trim()) {
        setRemoteCredentials((prev) => ({ ...prev, passwordError: true }));
        return;
      }

      if (!customerSelectedRow?.email) {
        setConnectionMsg("Customer email is missing");
        return;
      }

      // Persist current admin Customers grid state so we can restore it on EXIT
      try {
        const gridState = {
          page: customersPagination?.page ?? 0,
          pageSize: customersPagination?.rowsPerPage ?? 5,
          selectedCustomerId: customerSelectedRow?.id ?? null,
        };
        sessionStorage.setItem(
          "adminRemoteCustomersState",
          JSON.stringify(gridState),
        );
      } catch (e) {
        // ignore storage errors
      }

      // Validate remote credentials
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remote-connection`,
        {
          currentSession: currentSession,
          email: customerSelectedRow.email,
          username: remoteCredentials.username.trim(),
          password: remoteCredentials.password.trim(),
          currentPath: window.location.pathname,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        setConnectionMsg("Credentials validated! Creating remote session...");

        // Create the remote session in the SAME tab
        const sessionResp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remote-connection-session`,
          {
            currentSession: currentSession,
            target_email: customerSelectedRow.email,
            currentPath: window.location.pathname,
          },
          { withCredentials: true },
        );

        if (sessionResp?.data?.success) {
          // Mark this browser tab as being in an admin remote session
          try {
            sessionStorage.setItem("adminRemote", "true");
            if (currentUser?.user?.email) {
              sessionStorage.setItem(
                "adminRemoteEmail",
                currentUser.user.email,
              );
            }
          } catch (e) {
            // ignore storage errors
          }

          // Navigate to dashboard as the remote user
          const baseUrl = window.location.origin;
          window.location.href = `${baseUrl}/dashboard`;
        } else {
          setConnectionMsg(
            sessionResp?.data?.message ||
              "Failed to create remote session. Please try again.",
          );
        }
      } else {
        setConnectionMsg(
          resp?.data?.message || "Connection failed. Please try again.",
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Connection failed. Please try again.";
      setConnectionMsg(errorMessage);
      console.error("Remote connection error:", error);
    } finally {
      setConnectionLoading(false);
      setTimeout(() => {
        setConnectionMsg("");
      }, 8000);
    }
  };

  const handleRemove2fa = async () => {
    try {
      if (!customerSelectedRow?.email) {
        setRemove2faMsg("Customer email is missing");
        return;
      }

      setRemove2faLoading(true);
      setRemove2faMsg("");

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/remove-user-2fa`,
        {
          currentSession,
          email: customerSelectedRow.email,
          currentPath: window.location.pathname,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        setCustomerSelectedRow((prev) =>
          prev
            ? {
                ...prev,
                mfa: false,
                faSet: false,
                auth_type: null,
                memorable_option: undefined,
                memorable_value: undefined,
              }
            : prev,
        );
        setRemove2faDialogOpen(false);
      } else {
        setRemove2faMsg(resp?.data?.message || "Failed to disable 2FA");
      }
    } catch (error) {
      setRemove2faMsg(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to disable 2FA",
      );
    } finally {
      setRemove2faLoading(false);
    }
  };

  const handleConvertToEnterprise = async () => {
    try {
      if (!customerSelectedRow?.email) {
        setConvertEnterpriseMsg("Customer email is missing");
        return;
      }

      setConvertEnterpriseLoading(true);
      setConvertEnterpriseMsg("");

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/convert-user-to-enterprise`,
        {
          currentSession,
          email: customerSelectedRow?.email,
          currentPath: window.location.pathname,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        const updatedRow = {
          ...customerSelectedRow,
          tierName: "Enterprise",
          user_plan: {
            ...customerSelectedRow?.user_plan,
            title: "Enterprise",
          },
          tableDatas: {
            ...customerSelectedRow?.tableDatas,
            tableDataPlan: customerSelectedRow?.tableDatas?.tableDataPlan?.map(
              (row) =>
                row.label === "Current Base Plan"
                  ? { ...row, value: "Enterprise" }
                  : row,
            ),
          },
        };

        dispatch(updateCustomersRow(updatedRow));
        setCustomerSelectedRow((prev) =>
          prev
            ? {
                ...prev,
                tierName: "Enterprise",
                user_plan: {
                  ...prev.user_plan,
                  title: "Enterprise",
                },
                tableDatas: {
                  ...prev.tableDatas,
                  tableDataPlan: prev?.tableDatas?.tableDataPlan?.map((row) =>
                    row.label === "Current Base Plan"
                      ? { ...row, value: "Enterprise" }
                      : row,
                  ),
                },
              }
            : prev,
        );
        setConvertEnterpriseDialogOpen(false);
      } else {
        setConvertEnterpriseMsg(
          resp?.data?.message || "Failed to convert account to Enterprise",
        );
      }
    } catch (error) {
      setConvertEnterpriseMsg(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to convert account to Enterprise",
      );
    } finally {
      setConvertEnterpriseLoading(false);
    }
  };

  useEffect(() => {
    setTables((prevTables) => {
      // Only update if customerSelectedRow actually changed (different ID)
      const currentRowId = customerSelectedRow?.id;
      const prevRowId = prevTables[0]?.rowId;

      if (currentRowId === prevRowId) {
        // Same row, just update the data without changing structure
        return prevTables.map((table) => {
          switch (table.header) {
            case "Permit Packages":
              return { ...table, data: customerSelectedRow?.permitPackages };
            case "Personal":
              return {
                ...table,
                data: customerSelectedRow?.tableDatas?.tableDataPersonal,
              };
            case "Plan":
              return {
                ...table,
                data: customerSelectedRow?.tableDatas?.tableDataPlan,
              };
            case "Usage":
              return {
                ...table,
                data: customerSelectedRow?.tableDatas?.tableDataUsage,
              };
            case "Contractors":
              return { ...table, data: customerSelectedRow?.contractors };
            case "Coupons":
              return {
                ...table,
                data: customerSelectedRow?.user_discounts,
                coupons: customerSelectedRow?.coupons,
              };
            case "Permit Package Usage Chart":
              return { ...table, data: customerSelectedRow?.ppUsage };
            case "Notice of Commencement Usage Chart":
              return { ...table, data: customerSelectedRow?.nocUsage };
            default:
              return table;
          }
        });
      } else {
        // Different row, create new structure
        return [
          {
            name: "permitPackages",
            header: "Permit Packages",
            open: false,
            data: customerSelectedRow?.permitPackages,
            rowId: currentRowId,
          },
          {
            header: "Personal",
            open: false,
            data: customerSelectedRow?.tableDatas?.tableDataPersonal,
            rowId: currentRowId,
          },
          {
            header: "Plan",
            open: false,
            data: customerSelectedRow?.tableDatas?.tableDataPlan,
            rowId: currentRowId,
          },
          {
            header: "Usage",
            open: false,
            data: customerSelectedRow?.tableDatas?.tableDataUsage,
            rowId: currentRowId,
          },
          {
            header: "Contractors",
            open: false,
            data: customerSelectedRow?.contractors,
            rowId: currentRowId,
          },
          {
            header: "Coupons",
            open: false,
            data: customerSelectedRow?.user_discounts,
            coupons: customerSelectedRow?.coupons,
            rowId: currentRowId,
          },
          {
            name: "ppUsage",
            header: "Permit Package Usage Chart",
            open: false,
            data: customerSelectedRow?.ppUsage,
            rowId: currentRowId,
          },
          {
            name: "nocUsage",
            header: "Notice of Commencement Usage Chart",
            open: false,
            data: customerSelectedRow?.nocUsage,
            rowId: currentRowId,
          },
        ];
      }
    });
  }, [customerSelectedRow]);

  const toggleTableOpen = (header) => {
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.header === header ? { ...table, open: !table.open } : table,
      ),
    );
  };

  const isTableOpen = (header) => {
    const table = tables.find((table) => table.header === header);
    return table ? table.open : false; // Returns `false` if not found
  };

  const isMfaEnabled = !!customerSelectedRow?.faSet;
  const isAdminTarget = customerSelectedRow?.role === "admin";
  const isCurrentUserAdmin =
    currentUser?.user?.role === "admin" && !!currentUser?.user?.admin;
  const isEnterpriseTier = customerSelectedRow?.tierName === "Enterprise";
  const isSubAdminTarget = isSubAdminEmail(
    customerSelectedRow?.email,
    subAdminDomains,
  );
  const hasContractors = customerSelectedRow?.contractors?.length > 0;
  const isOnFreePlan = customerSelectedRow?.user_plan?.title !== "Free";

  const isEnterpriseActionDisabled =
    isAdminTarget ||
    isSubAdminTarget ||
    isEnterpriseTier ||
    hasContractors ||
    isOnFreePlan;

  const enterpriseTooltipTitle = isEnterpriseTier
    ? "This user is already on the Enterprise tier"
    : isAdminTarget
      ? "Converting admin accounts to Enterprise is not allowed"
      : isSubAdminTarget
        ? "Converting sub-admin accounts to Enterprise is not allowed"
        : hasContractors
          ? "Converting accounts with contractors to Enterprise is not allowed"
          : isOnFreePlan
            ? "Only users on the Free plan can be converted to Enterprise"
            : "Convert account to Enterprise";

  return (
    <Box sx={{ mt: "1rem" }} ref={customerInfoCardRef}>
      {mobileSize && (
        <Box
          sx={{ borderTop: "4px solid #d3d3d3", width: "100%", height: 0 }}
        />
      )}
      <Card
        sx={{
          ...(mobileSize
            ? { boxShadow: "none !important", borderRadius: "0px !important" }
            : {}),
        }}
      >
        <CardContent sx={{ pt: 2, px: mobileSize ? 0 : tabletSize ? 1.5 : 3 }}>
          <Box mb={3} textAlign="center">
            <Typography variant="h5" mb={0} gutterBottom>
              {customerSelectedRow?.customerName}
            </Typography>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
            >
              {customerSelectedRow?.email}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                mt: 1,
              }}
            >
              <Tooltip
                title={
                  isAdminTarget
                    ? "Remote connect to admin accounts is not allowed"
                    : "Connect to account"
                }
              >
                <span>
                  <IconButton
                    aria-label="Open remote connection modal"
                    onClick={() => setRemoteModalOpen(true)}
                    disabled={isAdminTarget}
                    sx={{
                      color: "success.main",
                      border: "1px solid",
                      borderColor: "success.main",
                      "&.Mui-disabled": {
                        borderColor: "action.disabled",
                      },
                    }}
                  >
                    <PhonelinkSetupIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  isAdminTarget
                    ? "Removing 2FA for admin accounts is not allowed"
                    : isMfaEnabled
                      ? "Remove 2FA"
                      : "MFA is turned off for this user"
                }
              >
                <span>
                  <IconButton
                    aria-label="Remove user 2FA"
                    onClick={() => setRemove2faDialogOpen(true)}
                    disabled={!isMfaEnabled || isAdminTarget}
                    sx={{
                      color: "error.main",
                      border: "1px solid",
                      borderColor: "error.main",
                      "&.Mui-disabled": {
                        borderColor: "action.disabled",
                      },
                    }}
                  >
                    <NoEncryptionIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {isCurrentUserAdmin && (
                <Tooltip title={enterpriseTooltipTitle}>
                  <span>
                    <IconButton
                      aria-label="Convert account to Enterprise"
                      onClick={() => setConvertEnterpriseDialogOpen(true)}
                      disabled={isEnterpriseActionDisabled}
                      sx={{
                        color: "warning.main",
                        border: "1px solid",
                        borderColor: "warning.main",
                        "&.Mui-disabled": {
                          borderColor: "action.disabled",
                        },
                      }}
                    >
                      <WorkspacePremiumIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            {!!remove2faMsg && (
              <Typography variant="body2" sx={{ mt: 1 }} color="error">
                {remove2faMsg}
              </Typography>
            )}

            {!!convertEnterpriseMsg && (
              <Typography variant="body2" sx={{ mt: 1 }} color="error">
                {convertEnterpriseMsg}
              </Typography>
            )}
          </Box>

          <Grid container spacing={2}>
            {tables.map((table) => (
              <Grid
                key={table?.header}
                size={{
                  xs: 12,
                  sm:
                    table?.header === "Coupons" ||
                    ["ppUsage", "nocUsage", "permitPackages"].includes(
                      table?.name,
                    )
                      ? 12
                      : tabletSize
                        ? 12
                        : 6,
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ position: "relative" }}>
                          <TableCell
                            sx={
                              table?.header === "Coupons" ||
                              ["ppUsage", "nocUsage"].includes(table?.name)
                                ? {
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    width: "100%",
                                    height: "40px",
                                  }
                                : { fontWeight: "bold" }
                            }
                          >
                            {table?.header}
                          </TableCell>
                          <TableCell
                            align={
                              table?.header !== "Coupons" &&
                              !["ppUsage", "nocUsage"].includes(table?.name)
                                ? "right"
                                : undefined
                            }
                            sx={
                              table?.header === "Coupons" ||
                              ["ppUsage", "nocUsage"].includes(table?.name)
                                ? { position: "absolute", right: 16 }
                                : {}
                            }
                          >
                            <IconButton
                              onClick={() => toggleTableOpen(table?.header)}
                            >
                              {isTableOpen(table?.header) ? (
                                <RemoveCircleIcon color="error" />
                              ) : (
                                <AddCircleIcon color="primary" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{
                              p: 0,
                            }}
                          >
                            <Collapse
                              in={isTableOpen(table?.header)}
                              timeout="auto"
                              unmountOnExit
                            >
                              {table?.header === "Contractors" ? (
                                <RenderCustomerContractors
                                  cardPage={cardPage}
                                  setCardPage={setCardPage}
                                  table={table}
                                />
                              ) : table?.header === "Coupons" ? (
                                <RenderDiscounts
                                  discountPage={discountPage}
                                  setDiscountPage={setDiscountPage}
                                  table={table}
                                  customerSelectedRow={customerSelectedRow}
                                  setSelectedRow={setCustomerSelectedRow}
                                />
                              ) : ["ppUsage", "nocUsage"].includes(
                                  table?.name,
                                ) ? (
                                <RenderUsageChart
                                  table={table}
                                  customerSelectedRow={customerSelectedRow}
                                />
                              ) : table?.name === "permitPackages" ? (
                                <RenderCustomerPermits
                                  packagePage={packagePage}
                                  setPackagePage={setPackagePage}
                                  packagePageSize={packagePageSize}
                                  setpackagePageSize={setpackagePageSize}
                                  packageLoading={packageLoading}
                                  setPackageLoading={setPackageLoading}
                                  customerSelectedRow={customerSelectedRow}
                                  packageRowCount={packageRowCount}
                                  setPackageRowCount={setPackageRowCount}
                                  selectedPackage={selectedPackage}
                                  setSelectedPackage={setSelectedPackage}
                                />
                              ) : (
                                <Table>
                                  <TableBody>
                                    {table?.data?.map((row, index) => (
                                      <TableRow key={index}>
                                        {mobileSize ? (
                                          <TableCell component="th" scope="row">
                                            <Typography
                                              sx={{
                                                fontWeight: "bold",
                                                fontSize: "inherit",
                                              }}
                                            >
                                              {row.label}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-all",
                                                fontSize: "inherit",
                                              }}
                                            >
                                              {row.value}
                                            </Typography>
                                          </TableCell>
                                        ) : (
                                          <>
                                            <TableCell
                                              component="th"
                                              scope="row"
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              {row.label}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-all",
                                              }}
                                            >
                                              {row.value}
                                            </TableCell>
                                          </>
                                        )}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
      <RemoteConnectionModal
        remoteModalOpen={remoteModalOpen}
        closeRemoteModal={closeRemoteModal}
        customerSelectedRow={customerSelectedRow}
        remoteCredentials={remoteCredentials}
        setRemoteCredentials={setRemoteCredentials}
        handleRemoteConnect={handleRemoteConnect}
        connectionLoading={connectionLoading}
        connectionMsg={connectionMsg}
      />

      <Dialog
        open={remove2faDialogOpen}
        onClose={
          remove2faLoading ? undefined : () => setRemove2faDialogOpen(false)
        }
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { ...(mobileSize ? { margin: 0, width: "100%" } : {}) },
          },
        }}
      >
        <DialogTitle>Remove Two-Factor Authentication?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove 2FA for
            {` ${customerSelectedRow?.email || "this user"}`}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemove2faDialogOpen(false)}
            disabled={remove2faLoading}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove2fa}
            disabled={remove2faLoading}
          >
            {remove2faLoading ? "Removing..." : "Yes, remove 2FA"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={convertEnterpriseDialogOpen}
        onClose={
          convertEnterpriseLoading
            ? undefined
            : () => setConvertEnterpriseDialogOpen(false)
        }
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { ...(mobileSize ? { margin: 0, width: "100%" } : {}) },
          },
        }}
      >
        <DialogTitle>Convert Account to Enterprise?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to convert
            {` ${customerSelectedRow?.email || "this user"}`} to the Enterprise
            tier?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConvertEnterpriseDialogOpen(false)}
            disabled={convertEnterpriseLoading}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleConvertToEnterprise}
            disabled={convertEnterpriseLoading}
          >
            {convertEnterpriseLoading ? "Converting..." : "Yes, convert"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerInfoCard;
